import { useState } from 'react';
import { X, DollarSign, Calendar, ArrowRight, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';

interface EncaissementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onValidate: (encaissementDate: string) => void;
}

export default function EncaissementModal({ isOpen, onClose, onValidate }: EncaissementModalProps) {
  const { user } = useAuth();
  const [encaissementDate, setEncaissementDate] = useState('');
  const [error, setError] = useState('');

  useEscapeKey(() => {
    if (isOpen) {
      onClose();
    }
  });

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!encaissementDate) {
      setError('La date d\'encaissement est obligatoire');
      return;
    }

    onValidate(encaissementDate);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <form
        onSubmit={handleSubmit}
        className="bg-white w-full max-w-md rounded-2xl flex flex-col overflow-hidden shadow-2xl"
      >
        {/* ── Header ── */}
        <div className="bg-gradient-to-r from-slate-700 to-slate-900 px-6 py-5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                <DollarSign size={20} className="text-white" />
              </div>
              <div>
                <h2 className="text-base font-bold text-white">Passage en encaissé</h2>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">Acte</span>
                  <ArrowRight size={10} className="text-slate-400" />
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">Encaissé</span>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 rounded-xl bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors shrink-0"
            >
              <X size={18} className="text-white" />
            </button>
          </div>
        </div>

        {/* ── Content ── */}
        <div className="p-6 space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Date d'encaissement
            </label>
            <div className="relative">
              <Calendar size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                type="date"
                value={encaissementDate}
                onChange={(e) => setEncaissementDate(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400 transition-colors"
                required
              />
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm">
              <AlertCircle size={14} className="shrink-0" />
              {error}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="border-t border-slate-100 px-6 py-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            className="px-6 py-2.5 text-sm font-semibold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 disabled:opacity-50 transition-all shadow-sm"
          >
            Valider
          </button>
        </div>
      </form>
    </div>
  );
}